/**
 * Wordle Game Persistence Service
 *
 * Saves daily progress to prevent replay on the same day.
 * Stores game state for recovery if user leaves mid-game.
 */

import { WordleGameState, WordleGameMode } from '../types';

export interface SavedWordleGame {
  gameState: WordleGameState;
  metadata: {
    mode: WordleGameMode;
    puzzleNumber?: number;
    startedAt: number;
    lastSavedAt: number;
  };
}

export class WordlePersistence {
  private readonly DAILY_STATE_KEY = 'wordle_daily_state';
  private readonly PRACTICE_STATE_KEY = 'wordle_practice_state';
  private readonly DAILY_COMPLETED_KEY = 'wordle_daily_completed';

  /** Save the current game state */
  saveGameState(state: WordleGameState): boolean {
    try {
      const key = state.mode === 'daily' ? this.DAILY_STATE_KEY : this.PRACTICE_STATE_KEY;
      const savedGame: SavedWordleGame = {
        gameState: state,
        metadata: {
          mode: state.mode,
          puzzleNumber: state.puzzleNumber,
          startedAt: state.startedAt,
          lastSavedAt: Date.now(),
        },
      };
      localStorage.setItem(key, JSON.stringify(savedGame));
      return true;
    } catch (error) {
      console.error('Failed to save wordle game state:', error);
      return false;
    }
  }

  /** Load a saved game state */
  loadGameState(mode: WordleGameMode): SavedWordleGame | null {
    try {
      const key = mode === 'daily' ? this.DAILY_STATE_KEY : this.PRACTICE_STATE_KEY;
      const data = localStorage.getItem(key);
      if (!data) return null;

      const saved = JSON.parse(data) as SavedWordleGame;

      // For daily mode, check if it's still the same puzzle
      if (mode === 'daily' && saved.metadata.puzzleNumber !== undefined) {
        const currentPuzzle = this.getCurrentPuzzleNumber();
        if (saved.metadata.puzzleNumber !== currentPuzzle) {
          // Old puzzle, clear it
          this.clearGameState(mode);
          return null;
        }
      }

      return saved;
    } catch (error) {
      console.error('Failed to load wordle game state:', error);
      return null;
    }
  }

  /** Clear a saved game state */
  clearGameState(mode: WordleGameMode): void {
    const key = mode === 'daily' ? this.DAILY_STATE_KEY : this.PRACTICE_STATE_KEY;
    localStorage.removeItem(key);
  }

  /** Mark today's daily puzzle as completed */
  markDailyCompleted(puzzleNumber: number): void {
    try {
      localStorage.setItem(this.DAILY_COMPLETED_KEY, JSON.stringify({
        puzzleNumber,
        completedAt: Date.now(),
      }));
    } catch (error) {
      console.error('Failed to mark daily as completed:', error);
    }
  }

  /** Check if today's daily puzzle has been completed */
  isDailyCompleted(): boolean {
    try {
      const data = localStorage.getItem(this.DAILY_COMPLETED_KEY);
      if (!data) return false;

      const { puzzleNumber } = JSON.parse(data);
      return puzzleNumber >= this.getCurrentPuzzleNumber();
    } catch {
      return false;
    }
  }

  /** Get the completed daily game state (for showing results) */
  getCompletedDailyState(): WordleGameState | null {
    try {
      const data = localStorage.getItem(this.DAILY_STATE_KEY);
      if (!data) return null;
      const saved = JSON.parse(data) as SavedWordleGame;
      if (saved.gameState.gameStatus === 'won' || saved.gameState.gameStatus === 'lost') {
        return saved.gameState;
      }
      return null;
    } catch {
      return null;
    }
  }

  private getCurrentPuzzleNumber(): number {
    const EPOCH_DATE = new Date(2024, 0, 1).getTime();
    const MS_PER_DAY = 86400000;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.floor((today - EPOCH_DATE) / MS_PER_DAY);
  }
}
